import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import type { CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

type CustomDismissCallback = (messageInstanceId: string) => void;

// This type will be the contract between the message and the placement
export type placementRenderProps = { dismiss: () => void; messageInstanceId: string };

// We use the CustomRenderer type to tell the placement that these props are
// going to be used for renderering.
type placementDefinition = CustomRenderer<placementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: `jsm-responders-update`,
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
	},
};

const placement = {
	createFrontend: createPlacementFrontend<placementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<placementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<placementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<placementRenderProps>;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);

	const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

	useEffect(() => {
		const { error, data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}

		setMessages(data.messages);
	}, [apiResponse]);

	const handleDismiss: CustomDismissCallback = (messageInstanceId) => {
		// On dismiss we remove the message from the local state using the messageInstanceId.
		setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
	};

	return (
		<Stack space="space.150">
			{!apiResponse.isLoading && (
				<Stack as="ul" space="space.200">
					{messages.map((props) => (
						<Inline as="li" key={props.messageInstanceId}>
							<Messages.render dismiss={() => handleDismiss(props.messageInstanceId)} {...props} />
						</Inline>
					))}
				</Stack>
			)}
		</Stack>
	);
});
